import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const AdminSidebar = ({ isCollapsed = false }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['dashboard']);
  const [expandedNestedSections, setExpandedNestedSections] = useState([]);

  const navigationSections = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '/super-admin-dashboard',
      subsections: []
    },
    {
      id: 'clients',
      label: 'Client Management',
      icon: 'Users',
      path: '/super-admin/clients',
      subsections: [
        { label: 'All Clients', path: '/super-admin/clients', icon: 'List' },
        { label: 'Activity Logs', path: '/super-admin/clients/activity', icon: 'Activity' },
        { label: 'Suspended Users', path: '/super-admin/clients/suspended', icon: 'UserX' }
      ]
    },
    {
      id: 'subscriptions',
      label: 'Subscription & Pricing',
      icon: 'CreditCard',
      path: '/super-admin/subscriptions',
      subsections: [
        { label: 'All Plans', path: '/super-admin/subscriptions/plans', icon: 'Package' },
        { label: 'Create Plan', path: '/super-admin/subscriptions/create', icon: 'Plus' },
        { label: 'Plan Features', path: '/super-admin/subscriptions/features', icon: 'Settings' },
        { label: 'Pricing Rules', path: '/super-admin/subscriptions/pricing', icon: 'DollarSign' },
        { label: 'Features Matrix', path: '/super-admin-plan-features-matrix', icon: 'Grid' },
        { label: 'Pricing Configuration', path: '/super-admin-pricing-rules', icon: 'Sliders' }
      ]
    },
    {
      id: 'payments',
      label: 'Payment Management',
      icon: 'Wallet',
      path: '/super-admin/payments',
      subsections: [
        { label: 'Payment History', path: '/super-admin/payments/history', icon: 'History' },
        { label: 'Transactions', path: '/super-admin/payments/transactions', icon: 'ArrowLeftRight' },
        { label: 'Refunds', path: '/super-admin/payments/refunds', icon: 'RotateCcw' },
        { label: 'Invoices', path: '/super-admin/payments/invoices', icon: 'FileText' },
        { label: 'Gateway Settings', path: '/super-admin/payments/gateway', icon: 'Settings' }
      ]
    },
    {
      id: 'reports',
      label: 'Reports Management',
      icon: 'BarChart3',
      path: '/super-admin/reports',
      subsections: [
        { label: 'Generated Reports', path: '/super-admin/reports/generated', icon: 'FileCheck' },
        { label: 'Module Analytics', path: '/super-admin/reports/analytics', icon: 'TrendingUp' },
        {
          label: 'User Activity',
          icon: 'Users',
          subsections: [
            { label: 'Behavior Analytics', path: '/user-behavior-analytics-center', icon: 'Activity' },
            { label: 'Activity Monitoring', path: '/activity-monitoring-command-center', icon: 'Radio' }
          ]
        },
        { label: 'Export Data', path: '/super-admin/reports/export', icon: 'Download' }
      ]
    },
    {
      id: 'cms',
      label: 'Content Management',
      icon: 'FileText',
      path: '/super-admin/cms',
      subsections: [
        { label: 'Product Pages', path: '/super-admin/cms/product', icon: 'Box' },
        { label: 'Features', path: '/super-admin/cms/features', icon: 'Sparkles' },
        { label: 'Pricing Page', path: '/super-admin/cms/pricing', icon: 'DollarSign' },
        { label: 'Company Pages', path: '/super-admin/cms/company', icon: 'Building' },
        { label: 'Blog Management', path: '/super-admin/cms/blog', icon: 'BookOpen' },
        { label: 'Resources', path: '/super-admin/cms/resources', icon: 'Library' }
      ]
    },
    {
      id: 'legal',
      label: 'Legal CMS',
      icon: 'Scale',
      path: '/super-admin/legal',
      subsections: [
        { label: 'Privacy Policy', path: '/super-admin/legal/privacy', icon: 'Shield' },
        { label: 'Terms of Service', path: '/super-admin/legal/terms', icon: 'FileCheck' },
        { label: 'Cookie Policy', path: '/super-admin/legal/cookies', icon: 'Cookie' },
        { label: 'GDPR Compliance', path: '/super-admin/legal/gdpr', icon: 'Lock' }
      ]
    }
  ];

  const isActive = (path) => {
    if (!location || !path) return false;
    return location?.pathname === path;
  };

  const isSectionActive = (section) => {
    if (!section) return false;
    if (isActive(section?.path)) return true;

    // Check direct subsections
    if (section?.subsections && section?.subsections?.length > 0) {
      return section?.subsections?.some(sub => {
        if (sub?.path && isActive(sub?.path)) return true;
        // Check nested subsections
        if (sub?.subsections && sub?.subsections?.length > 0) {
          return sub?.subsections?.some(nested => isActive(nested?.path));
        }
        return false;
      });
    }
    return false;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev?.includes(sectionId)
        ? prev?.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleNestedSection = (subsectionLabel) => {
    setExpandedNestedSections(prev =>
      prev?.includes(subsectionLabel)
        ? prev?.filter(label => label !== subsectionLabel)
        : [...prev, subsectionLabel]
    );
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={handleMobileToggle}
        className="fixed top-20 left-4 z-50 lg:hidden p-2 rounded-md bg-slate-800 border border-slate-700 transition-smooth hover:bg-slate-700"
        aria-label="Toggle admin navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} color="#F59E0B" />
      </button>
      {/* Sidebar */}
      <aside
        className={`fixed lg:fixed top-0 left-0 h-full bg-slate-900 border-r border-slate-800 z-40 transition-smooth overflow-y-auto ${isCollapsed ? 'w-20' : 'w-64'
          } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-center h-20 border-b border-slate-800 bg-slate-950 transition-smooth px-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/50">
              <Icon name="Shield" size={isCollapsed ? 20 : 24} color="#F59E0B" />
            </div>
            {!isCollapsed && (
              <div className="ml-3">
                <span className="text-base font-heading font-bold text-white">
                  Super Admin
                </span>
                <p className="text-xs text-slate-400 font-caption">Control Panel</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-3" aria-label="Admin navigation">
            <ul className="space-y-1">
              {navigationSections?.map((section) => {
                const sectionActive = isSectionActive(section);
                const isExpanded = expandedSections?.includes(section?.id);
                const hasSubsections = section?.subsections?.length > 0;

                return (
                  <li key={section?.id}>
                    {/* Main Section Link */}
                    {hasSubsections ? (
                      <button
                        onClick={() => toggleSection(section?.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-smooth group ${sectionActive
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                          }`}
                        title={isCollapsed ? section?.label : undefined}
                      >
                        <div className="flex items-center">
                          <Icon
                            name={section?.icon}
                            size={20}
                            color={sectionActive ? '#FBBF24' : 'currentColor'}
                            strokeWidth={sectionActive ? 2.5 : 2}
                          />
                          {!isCollapsed && (
                            <span className="ml-3 text-sm font-medium font-body">
                              {section?.label}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <Icon
                            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                            size={16}
                            color="currentColor"
                          />
                        )}
                      </button>
                    ) : (
                      <Link
                        to={section?.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center px-3 py-2.5 rounded-lg transition-smooth group ${sectionActive
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                          }`}
                        aria-current={sectionActive ? 'page' : undefined}
                        title={isCollapsed ? section?.label : undefined}
                      >
                        <Icon
                          name={section?.icon}
                          size={20}
                          color={sectionActive ? '#FBBF24' : 'currentColor'}
                          strokeWidth={sectionActive ? 2.5 : 2}
                        />
                        {!isCollapsed && (
                          <span className="ml-3 text-sm font-medium font-body">
                            {section?.label}
                          </span>
                        )}
                      </Link>
                    )}

                    {/* Subsections */}
                    {hasSubsections && isExpanded && !isCollapsed && (
                      <ul className="mt-1 ml-4 pl-4 border-l border-slate-800 space-y-1">
                        {section?.subsections?.map((subsection, subIndex) => {
                          const hasNestedSubsections = subsection?.subsections && subsection?.subsections?.length > 0;
                          const isNestedExpanded = expandedNestedSections?.includes(subsection?.label);

                          return (
                            <li key={subsection?.path || `${section?.id}-sub-${subIndex}`}>
                              {hasNestedSubsections ? (
                                <>
                                  <button
                                    onClick={() => toggleNestedSection(subsection?.label)}
                                    className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-smooth text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                                  >
                                    <div className="flex items-center">
                                      <Icon
                                        name={subsection?.icon}
                                        size={16}
                                        color="currentColor"
                                      />
                                      <span className="ml-2 font-body">{subsection?.label}</span>
                                    </div>
                                    <Icon
                                      name={isNestedExpanded ? 'ChevronDown' : 'ChevronRight'}
                                      size={14}
                                      color="currentColor"
                                    />
                                  </button>
                                  {isNestedExpanded && (
                                    <ul className="mt-1 ml-4 pl-3 border-l border-slate-700 space-y-1">
                                      {subsection?.subsections?.map((nested, nestedIndex) => (
                                        <li key={nested?.path || `nested-${nestedIndex}`}>
                                          <Link
                                            to={nested?.path}
                                            onClick={closeMobileMenu}
                                            className={`flex items-center px-2 py-1.5 rounded-md text-xs transition-smooth ${isActive(nested?.path)
                                                ? 'bg-slate-800 text-amber-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                              }`}
                                            aria-current={isActive(nested?.path) ? 'page' : undefined}
                                          >
                                            <Icon
                                              name={nested?.icon}
                                              size={14}
                                              color="currentColor"
                                            />
                                            <span className="ml-2 font-body">{nested?.label}</span>
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              ) : (
                                <Link
                                  to={subsection?.path}
                                  onClick={closeMobileMenu}
                                  className={`flex items-center px-3 py-2 rounded-md text-sm transition-smooth ${isActive(subsection?.path)
                                      ? 'bg-slate-800 text-amber-400' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                                    }`}
                                  aria-current={isActive(subsection?.path) ? 'page' : undefined}
                                >
                                  <Icon
                                    name={subsection?.icon}
                                    size={16}
                                    color="currentColor"
                                  />
                                  <span className="ml-2 font-body">{subsection?.label}</span>
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-slate-800 bg-slate-950">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'px-3'} py-2`}>
              <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                <Icon name="User" size={18} color="#F59E0B" />
              </div>
              {!isCollapsed && (
                <div className="ml-3">
                  <p className="text-sm font-medium text-white font-body">Super Admin</p>
                  <p className="text-xs text-slate-400 font-caption">
                    admin@businessjadhak.com
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default AdminSidebar;